import { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { EMBRYO_METHOD_OPTIONS, FRESH_FROZEN } from '@/constants';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { embryoNameFormatter } from '@/grid/value-formatters/embryoNameFormatter';

export const outsideEmbryosColumns: ColDef[] = [
  {
    headerName: '',
    width: 50,
    checkboxSelection: true,
    sortable: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
    valueFormatter: embryoNameFormatter,
  },
  {
    field: 'creation',
    headerName: 'Created On',
    width: 110,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    valueFormatter: dateOnlyFormatter,
    sortable: true,
  },
  {
    field: 'freezeSiteName',
    headerName: 'Freeze Site',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'freezeStorageLocationName',
    headerName: 'Storage Location',
    width: 130,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'method',
    headerName: 'Freeze Method',
    width: 110,
    filter: 'agSetColumnFilter',
    wrapHeaderText: true,
    filterParams: {
      values: EMBRYO_METHOD_OPTIONS.map((opt) => opt.value),
      valueFormatter: (params: ValueFormatterParams) => {
        return (
          EMBRYO_METHOD_OPTIONS.find((opt) => opt.value === params.value)
            ?.label ?? ''
        );
      },
      buttons: ['apply', 'clear'],
      closeOnApply: true,
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return (
        EMBRYO_METHOD_OPTIONS.find((opt) => opt.value === params.value)
          ?.label ?? ''
      );
    },
    sortable: true,
  },
  {
    field: 'freshFrozen',
    headerName: 'Fresh/Frozen',
    width: 110,
    filter: 'agSetColumnFilter',
    wrapHeaderText: true,
    filterParams: {
      values: [FRESH_FROZEN.Fresh, FRESH_FROZEN.Frozen],
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === FRESH_FROZEN.Fresh) return 'Fresh';
        if (params.value === FRESH_FROZEN.Frozen) return 'Frozen';
        return '';
      },
      buttons: ['apply', 'clear'],
      closeOnApply: true,
    },
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value === null || params.value === undefined) return '';
      if (params.value === FRESH_FROZEN.Fresh) return 'Fresh';
      if (params.value === FRESH_FROZEN.Frozen) return 'Frozen';
      return '';
    },
    sortable: true,
  },
  {
    field: 'donorReg',
    headerName: 'Reg Number',
    width: 110,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'donorName',
    headerName: 'Donor',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'donorBreed',
    headerName: 'Donor Breed',
    width: 110,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'sireName',
    headerName: 'Sire',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'sireBreedName',
    headerName: 'Sire Breed',
    width: 110,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'naabCode',
    headerName: 'Sire Naab',
    width: 110,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'freezeOwnerName',
    headerName: 'Freeze Owner',
    width: 130,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'transferOwnerName',
    headerName: 'Transfer Owner',
    width: 130,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'transferSiteName',
    headerName: 'Transfer Site',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'freezeCane',
    headerName: 'Freeze Cane',
    width: 100,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'freezeQuantity',
    headerName: 'Freeze Quantity',
    width: 80,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'transferQuantity',
    headerName: 'Transfer Quantity',
    width: 80,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'freezeTt',
    headerName: 'TT',
    width: 80,
    filter: 'agSetColumnFilter',
    wrapHeaderText: true,
    filterParams: {
      values: [true, false],
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === true) return 'Yes';
        if (params.value === false) return 'No';
        return '';
      },
      buttons: ['apply', 'clear'],
      closeOnApply: true,
    },
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value === null || params.value === undefined) return '';
      return params.value ? 'Yes' : 'No';
    },
    sortable: true,
  },
  {
    field: 'receivedOn',
    headerName: 'Received Date',
    width: 120,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    valueFormatter: dateOnlyFormatter,
    sortable: true,
  },
  {
    field: 'recoveryFirmCode',
    headerName: 'Recovery Firm Code',
    width: 80,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    sortable: true,
  },
  {
    field: 'stateCode',
    headerName: 'Active',
    width: 100,
    filter: 'agSetColumnFilter',
    wrapHeaderText: true,
    filterParams: {
      values: [0, 1],
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === 0) return 'Active';
        if (params.value === 1) return 'Inactive';
        return '';
      },
      buttons: ['apply', 'clear'],
      closeOnApply: true,
    },
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value === null || params.value === undefined) return '';
      return params.value === 0 ? 'Active' : 'Inactive';
    },
    sortable: true,
  },
];
